/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { type PropsWithChildren } from 'react';

import { cssMap, jsx } from '@compiled/react';
import Markdown from 'markdown-to-jsx';
import { graphql, useFragment } from 'react-relay';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { removeMarkdown } from '../../utils/remove-markdown';
import type { messageBody_AtlassianSearchPage$key } from './__generated__/messageBody_AtlassianSearchPage.graphql';
import { Channel, type ChannelProps } from './channel';
import { MentionText, UserMention, type UserMentionProps } from './mention';

const styles = cssMap({
	title: {
		display: 'inline-block',
		maxWidth: '200px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		verticalAlign: 'bottom',
		color: token('color.text.subtle'),
	},
	separator: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

/**
 * The whitespace in between aliases <!here> <!channel> is removed
 * This constant prevent the alias from being pushed together ex: "@here@channel"
 * We are using this constant rather than adding a space through CSS because it adds
 * a real space character that does not break up the text when selecting.
 * */
const SPACE = ' ';

export const processMessageBody: (inputString: string) => string = (
	inputString: string,
): string => {
	if (!inputString) {
		return '';
	}

	const processed = removeMarkdown(inputString)
		// removing * or _, which can cause massive slowdown when processed by markdown-to-jsx's Markdown component
		.replace(/[*_]/g, '')
		//Slack User
		.replace(/<@(\w+)>/g, '<Mention>$1</Mention>')
		//Teams User
		.replace(/<@([a-zA-Z0-9-]+)>/g, '<Mention>$1</Mention>')
		//Slack Channel
		.replace(/<#(\w+)\|?>/g, '<Channel>$1</Channel>')
		//Teams Channel variation 1
		.replace(
			/<#teamId\/[a-f0-9-]+\/channelId\/teamId\/([a-f0-9-]+)\/channelId\/([0-9a-zA-Z:@._-]+)>/g,
			'<Channel>teamId/$1/channelId/$2</Channel>',
		)
		// Teams Channel variation 2
		.replace(
			/<[@#]teamId\/([a-f0-9-]+)\/channelId\/([0-9a-zA-Z:@._-]+)>/g,
			'<Channel>teamId/$1/channelId/$2</Channel>',
		)
		.replace(/<\!subteam\^(\w+)>/g, '<Subteam>$1</Subteam>')
		.replace(/<\!(\w+)>/g, '<Alias>$1</Alias>')
		// emojis, works for those that have repeating pattern as well. eg: :word-word-word:
		.replace(/:((@@@hl@@@.+?@@@endhl@@@)(-@@@hl@@@.+?@@@endhl@@@)*):/g, (match) => {
			const words =
				match
					.match(/@@@hl@@@(.+?)@@@endhl@@@/g)
					?.map((m) => m.replace(/@@@hl@@@|@@@endhl@@@/g, '')) || [];
			return `<Highlight>:${words.join('-')}:</Highlight>`;
		})
		.replace(/@@@hl@@@(.+?)@@@endhl@@@/g, '<Highlight>$1</Highlight>')
		.replace(/<@<Highlight>(.+?)<\/Highlight>>/g, '<Highlight><Mention>$1</Mention></Highlight>')
		.replace(/<#<Highlight>(.+?)<\/Highlight>\|>/g, '<Highlight><Channel>$1</Channel></Highlight>');
	// .replace(/\:(\w+)\:/g, '<Emoji>$1</Emoji>') Support for emojis coming soon

	// Check if the processed string starts with a channel tag and replace the first occurrence with ChannelWithoutSpace
	if (processed.startsWith('<Highlight><Channel>')) {
		return processed
			.replace('<Highlight><Channel>', '<HighlightWithoutSpace><ChannelWithoutSpace>')
			.replace('</Channel></Highlight>', '</ChannelWithoutSpace></HighlightWithoutSpace>');
	}

	// Check if the processed string starts with a highlight tag and replace the first occurrence with HighlightWithoutSpace
	if (processed.startsWith('<Highlight>')) {
		return processed
			.replace('<Highlight>', '<HighlightWithoutSpace>')
			.replace('</Highlight>', '</HighlightWithoutSpace>');
	}

	return processed;
};

const PlainText = ({ children }: PropsWithChildren<void>) => <>{children}</>;
const Highlight = ({ children }: PropsWithChildren<void>) => (
	<Text as="strong">
		{SPACE}
		{children}
	</Text>
);

const HighlightWithoutSpace = ({ children }: PropsWithChildren<void>) => (
	<Text as="strong">{children}</Text>
);

const ChannelWithoutSpace = ({ children, ...props }: PropsWithChildren<any>) => (
	<Channel {...props} noSpace={true}>
		{children}
	</Channel>
);

// It is assumed that description is the full message and excerpt is a potentially truncated version
export const completeExcerptIfTruncated: (excerpt: string, description: string) => string = (
	excerpt: string,
	description: string,
): string => {
	if (excerpt.length === 0 || description.length === 0 || excerpt === description) {
		return excerpt;
	}

	var modifiedExcerpt = excerpt;

	// regex which has the starting tag of a mention, but not the end
	const startTagRegex = /<[@#][^> ]*$/g;
	const startTagMatch = modifiedExcerpt.match(startTagRegex);

	if (startTagMatch) {
		// theoretically, there should only be one match at max
		const partialMention = startTagMatch[startTagMatch.length - 1];
		// the partialMention can appear several times in the string. We need to find the correct one
		const partialMentionInExcerptCount = excerpt.split(partialMention).length - 1;
		const splitDescription = description.split(partialMention);
		// check if partial mention is found in description
		if (splitDescription.length >= 2) {
			const splitByClosingTag = splitDescription[partialMentionInExcerptCount].split('>');
			// check if closing tag is found
			if (splitByClosingTag.length >= 2) {
				const restOfString = splitByClosingTag[0];
				modifiedExcerpt = modifiedExcerpt + restOfString + '>';
			}
		}
	}

	// const endTagRegex = /^[^< ]*\.tacv2>/g; This works on channel mentions that I have observed
	// weaker regex which tries to capture the ending tag of a mention (assuming it always ends with >), but not the start
	const weakEndTagRegex = /^[^< ]*>/g;
	const weakEndTagMatch = modifiedExcerpt.match(weakEndTagRegex);

	if (weakEndTagMatch) {
		// theoretically, there should only be one match at max
		const partialMention = weakEndTagMatch[0];
		const splitDescription = description.split(partialMention);
		// partialMention not found in description
		if (splitDescription.length >= 2) {
			const splitByOpeningTag = splitDescription[0].split('<');
			// opening tag found
			if (splitByOpeningTag.length >= 2) {
				const restOfString = splitByOpeningTag[splitByOpeningTag.length - 1];
				modifiedExcerpt = '<' + restOfString + modifiedExcerpt;
			}
		}
	}

	return modifiedExcerpt;
};

const hlStartTag = '@@@hl@@@';

// Two-line layout: attach up to this many characters before the match.
const EXPANDED_LEADIN_CHARS = 100;

// Trim the leading context before `highlightIndex` to at most `maxLeadInChars`,
// ending on a word boundary and prefixing '...' when text was dropped.
const trimLeadIn = (body: string, highlightIndex: number, maxLeadInChars: number): string => {
	let cursorPosition = highlightIndex - 1;
	let numChars = 0;
	let prefix = '';
	let currentWord = '';
	let numWords = 0;

	while (numChars < maxLeadInChars && cursorPosition > 0) {
		currentWord = body[cursorPosition] + currentWord;
		cursorPosition--;
		numChars++;
		if (body[cursorPosition] === ' ') {
			prefix = currentWord + prefix;
			currentWord = '';
			numWords++;
		}
	}

	if (cursorPosition <= 0) {
		return body.substring(0, highlightIndex);
	} else if (numWords === 0) {
		return '...' + currentWord;
	}
	return '...' + prefix;
};

export const processHighlighting: (body: string) => string = (body: string): string => {
	// if cannot find hl tag, return the original body
	const highlightIndex = body.indexOf(hlStartTag);
	if (highlightIndex === -1) {
		return body;
	}

	const maxLeadInChars = EXPANDED_LEADIN_CHARS;
	const leadIn = trimLeadIn(body, highlightIndex, maxLeadInChars);
	// The whole tail (highlight + everything after it) is kept untrimmed.
	const tail = body.substring(highlightIndex);

	return leadIn + tail;
};

export interface MessageBodyProps {
	integrationId?: string | null;
	node: messageBody_AtlassianSearchPage$key;
}

export const MessageBody: ({ node, integrationId }: MessageBodyProps) => JSX.Element = ({
	node,
	integrationId,
}: MessageBodyProps) => {
	const data = useFragment(
		graphql`
			fragment messageBody_AtlassianSearchPage on SearchResultGraphDocument {
				...mention_AtlassianSearchPage_UserMention
				...channel_AtlassianSearchPage
				title
				excerpt
				entity {
					... on ExternalMessage {
						description
					}
					... on ExternalConversation {
						description
					}
				}
			}
		`,
		node,
	);

	// in ConversationSearchResult, data.excerpt is used as the body
	let excerpt = data.excerpt;
	if (data.excerpt) {
		excerpt = completeExcerptIfTruncated(data.excerpt, data?.entity?.description ?? '');
	}

	let title = data.title || '';
	const description = data?.entity?.description || '';
	let body = excerpt || description;
	// Render from the description so the highlight window can keep the match in view.
	if (!body.includes(hlStartTag) && description.includes(hlStartTag)) {
		body = description;
	}

	const isMicrosoftTeams = integrationId === 'ari:cloud:platform::integration/microsoft';
	const shouldShowTitle = title && isMicrosoftTeams;

	title = processHighlighting(title);
	body = processHighlighting(body);

	const markdownOptions = {
		forceInline: true,
		overrides: {
			a: PlainText,
			Highlight,
			HighlightWithoutSpace,
			Alias: MentionText,
			mark: PlainText,
			Mention: {
				component: UserMention,
				props: {
					integrationId,
					node: data,
				} satisfies UserMentionProps,
			},
			Channel: {
				component: Channel,
				props: {
					node: data,
				} satisfies ChannelProps,
			},
			ChannelWithoutSpace: {
				component: ChannelWithoutSpace,
				props: {
					node: data,
				} satisfies ChannelProps,
			},
		},
	};

	/**
	 * Even though we remove markdown from the body, we still use this component
	 * to render mentions and highlighted text */
	if (shouldShowTitle) {
		return (
			<>
				<Box as="span" xcss={styles.title}>
					<Markdown options={markdownOptions}>{processMessageBody(title)}</Markdown>
				</Box>
				<Box as="span" xcss={styles.separator}>
					·
				</Box>
				<Markdown options={markdownOptions}>{processMessageBody(body)}</Markdown>
			</>
		);
	}

	return <Markdown options={markdownOptions}>{processMessageBody(body)}</Markdown>;
};
